// The cursor half of the Productivity Chart panel: which bucket the fixed readout row is
// currently describing, whether the reader is holding it still, and the chart-container listeners
// that move it.
//
// None of this touches the chart's options, so it never triggers GenericChart's reactive options
// watch - which is the whole point of wiring the row from the chart instance directly.

import { computed, onBeforeUnmount, ref, watch, type Ref } from 'vue';
import type { Chart, Point, Series } from 'highcharts';
import { i18n } from '@/plugins/internationalization/i18n';
import type { ProductivityGranularity } from '@/plugins/store/actions/queries/active-groups';
import type { ProductivityBucket, ProductivityComparison } from '@/types/SectionLiveKpi';
import { CHART_CHROME_COLORS, CHART_SERIES_COLORS, COMPARISON_REFERENCE_TEXT_COLOR, WATER_NORM_TEXT_COLOR } from './productivityColors';
import { comparedAsBins } from './productivityComparison';
import type { CursorLabel } from './productivityCursorLabels';
import type { ProductivityEventTooltip } from './productivityEventTooltip';
import { productivityMetricGap, productivityMetricValue } from './productivityFormatting';
import { METRIC_KEYS, PRODUCTIVITY_METRICS, type MetricFlags, type MetricKey } from './productivityMetrics';
import { productivityOverlay } from './productivityOverlay';
import {
    latestReadingTimestamp,
    METRIC_FIGURE_FIELD,
    metricAnchorMs,
    readoutAt,
    referenceReadoutFrom,
    type MetricFigures,
    type ProductivityReadout,
    type ProductivityReferenceReadout,
} from './productivityReadout';

// Highcharts' SVG text renderer understands a small subset of HTML, `<span style>` among it, which
// is what lets one label carry more than one colour without becoming an HTML overlay.
function colouredLine(text: string, color: string): string {
    return `<span style="color:${color}">${text}</span>`;
}

export function useProductivityCursor(input: {
    buckets: Ref<ProductivityBucket[]>;
    granularity: Ref<ProductivityGranularity>;
    // What the chart is actually drawing - switched on, expressible at this granularity, and backed
    // by a Sensor where the metric needs one - so a label only quotes what is on screen, and the
    // resting reading only rests on a bucket that has something on it.
    toggles: Ref<MetricFlags>;
    // The loaded comparison reference and the start of the Production Group it is projected onto, or null
    // for either while none is drawn. Resolved here rather than handed in ready-made, so "what is
    // under the cursor" has one owner and the readout row and the on-chart labels cannot end up
    // describing two different points.
    comparison: Ref<ProductivityComparison | null>;
    comparisonProductionGroupStart: Ref<string | null>;
    eventTooltip: ProductivityEventTooltip;
}) {
    const chart = ref<Chart | null>(null);
    const readout = ref<ProductivityReadout | null>(null);
    // What the reference stood at the same number of days into its own run, for the same instant the
    // row and the labels are describing. Null whenever no reference is drawn.
    const referenceReadout = ref<ProductivityReferenceReadout | null>(null);

    // Click to hold the readout still. Reading a figure off the row means looking away from the
    // cursor, and the moment the eye moves the hand does too - so without this, the values being read
    // are gone before they have been read. A click pins the row where the cursor is; another click
    // elsewhere moves the pin there; the row's own release control (or clicking the same bucket again)
    // lets it follow the cursor once more.
    const pinnedTimestampMs = ref<number | null>(null);
    const isPinned = computed(() => pinnedTimestampMs.value !== null);

    // Whether the row is describing where the reader is pointing, or has fallen back to the latest
    // reading on its own. The row says which: figures that arrived because the cursor is on them and
    // figures that are merely the most recent ones read the same on the page, and only one of the two
    // is an answer to a question the reader asked.
    const isPointing = ref(false);

    // The instant the row is currently describing - wherever the cursor last was, or null while the
    // reader has not positioned it at all. A chart update (a toggle, a range or granularity change)
    // re-renders against this rather than snapping back, so the reader's place isn't lost by an
    // unrelated control - while null lets the resting state be resolved against whatever data the new
    // render actually holds, which is the only moment that data is known.
    let lastReadoutTimestampMs: number | null = null;

    function metricLabel(key: MetricKey): string {
        return i18n.global.t(PRODUCTIVITY_METRICS[key].labelKey) as string;
    }

    function metricUnit(key: MetricKey): string {
        const unitKey = PRODUCTIVITY_METRICS[key].unitKey(input.granularity.value);
        return unitKey === null ? '' : (i18n.global.t(unitKey) as string);
    }

    // A figure and what it is measured in, spaced by the locale rather than by this file. A metric
    // with no unit is its figure alone - Growth Index is a ratio against a target, not a measurement.
    function withUnit(value: string, unit: string): string {
        if (unit === '') return value;
        return i18n.global.t('views.monitoring.activeGroups.modals.productivity.cursorValue', { value, unit }) as string;
    }

    // How far the measured water sits from its norm, as a signed percentage - the figure the pair of
    // columns exists to convey, which a reader would otherwise be estimating by eye from two bar
    // heights. Only when both are present and the norm is non-zero, since a percentage of nothing is
    // not a deviation.
    function waterDeviationText(values: MetricFigures): string | null {
        if (!input.toggles.value.water || !input.toggles.value.waterNorm) return null;
        const { waterActual, waterNorm } = values;
        if (waterActual === null || waterNorm === null || waterNorm === 0) return null;
        const percent = ((waterActual - waterNorm) / waterNorm) * 100;
        if (!Number.isFinite(percent)) return null;
        const rounded = Math.round(percent);
        // Over the norm is drawn in the over-norm colour the columns themselves use, so the figure and
        // the segment it describes are the same blue; under it stays neutral, being the ordinary case.
        const color = rounded > 0 ? CHART_SERIES_COLORS.waterOverNorm : WATER_NORM_TEXT_COLOR;
        return colouredLine(`${rounded > 0 ? '+' : ''}${i18n.global.n(rounded, 'decimal')} %`, color);
    }

    // Shorthand for the shared per-metric formatter, which needs the granularity the chart is drawn
    // at - one metric states its figure in a different unit, and so to a different precision, at each.
    function format(key: MetricKey, value: number | null): string {
        return productivityMetricValue(key, value, input.granularity.value);
    }

    function gapText(key: MetricKey, actual: number | null, reference: number | null): string | null {
        return productivityMetricGap(key, actual, reference, input.granularity.value);
    }

    // The text each metric's on-chart cursor label carries: the same figure the readout row shows, plus
    // its unit, since a number floating beside a curve has no column heading to be read under. Growth
    // Index is deliberately unitless - it is a ratio against a target, not a measurement. Water reads
    // as "actual / norm" in one label rather than two: the two columns are drawn one behind the other,
    // so separate labels would land on the same pixel and mean nothing apart anyway - which is also
    // why Water Norm has no label of its own and falls through to null below.
    function cursorLabelText(key: MetricKey, values: MetricFigures): string | null {
        switch (key) {
            case 'weight':
                return values.weight === null ? null : withUnit(format('weight', values.weight), metricUnit('weight'));
            case 'dailyGain':
                return values.dailyGain === null ? null : withUnit(format('dailyGain', values.dailyGain), metricUnit('dailyGain'));
            case 'growthIndex':
                return values.growthIndex === null ? null : format('growthIndex', values.growthIndex);
            case 'water': {
                if (values.waterActual === null && values.waterNorm === null) return null;
                // Named, on their own lines, rather than "2,76 / 3,12". The two columns are drawn one
                // behind the other so a bare pair gives the reader no way to tell which figure is the
                // measurement and which is the target - and reversing them silently inverts the answer
                // to the only question the pair is here to settle.
                // Each line in its own series' colour, even though both sit in one label. A single ink
                // for the pair would put the measurement and the target in the same colour and undo the
                // naming above - the reader would still have to remember which was which.
                const lines: string[] = [];
                if (input.toggles.value.water) {
                    lines.push(
                        colouredLine(
                            i18n.global.t('views.monitoring.activeGroups.modals.productivity.cursorNamedValueWithUnit', {
                                metric: metricLabel('water'),
                                value: format('water', values.waterActual),
                                unit: metricUnit('water'),
                            }) as string,
                            PRODUCTIVITY_METRICS.water.color
                        )
                    );
                }
                if (input.toggles.value.waterNorm) {
                    // Not the norm column's own `#CBD5E1`: a fill that reads as "muted" behind a solid
                    // column is unreadable as 11px text on white. The darker step of the same neutral
                    // keeps it subordinate to the measurement without making it illegible.
                    lines.push(
                        colouredLine(
                            i18n.global.t('views.monitoring.activeGroups.modals.productivity.cursorNamedValue', {
                                metric: metricLabel('waterNorm'),
                                value: format('waterNorm', values.waterNorm),
                            }) as string,
                            WATER_NORM_TEXT_COLOR
                        )
                    );
                }
                const deviation = waterDeviationText(values);
                if (deviation !== null) lines.push(deviation);
                return lines.join('<br/>');
            }
            case 'deadCount':
                // Zero deaths is the overwhelmingly common case and says nothing worth putting on the
                // chart; the readout row still reports it for anyone who wants to confirm the zero.
                return values.deadCount === null || values.deadCount === 0
                    ? null
                    : withUnit(format('deadCount', values.deadCount), metricUnit('deadCount'));
            default:
                return null;
        }
    }

    /**
     * The reference's counterpart of the label above: the same composition, from the reference's own
     * figures, each followed by the signed gap to the Section's own. It is the gap that a reader picked
     * a reference for, and at these axis scales two curves five kilos apart and two fifteen kilos apart
     * look alike.
     *
     * No units. The line it is read against has already stated them, and the pairing only means
     * anything because the two figures are in the same one. Suppression matches the primary label's
     * exactly, including the dead count's silence at zero - a reference that lost none here says as
     * little as the Section losing none does.
     *
     * The gap is dropped where the metric is compared as bins rather than as a silhouette. It exists
     * because two curves five kilos apart and two fifteen kilos apart look alike at these axis
     * scales - and a pair of bars drawn side by side does not have that problem, so there the figure
     * beside its own bar says everything the reader came for. The readout row keeps the gap for every
     * metric: nothing is drawn there to measure, and its column heading says whose figure is whose.
     */
    function referenceCursorLabelText(key: MetricKey, reference: MetricFigures, actual: MetricFigures): string | null {
        const figure = (field: keyof MetricFigures) => {
            const gap = comparedAsBins(key) ? null : gapText(key, actual[field], reference[field]);
            return `${format(key, reference[field])}${gap === null ? '' : ` ${gap}`}`;
        };

        switch (key) {
            case 'weight':
            case 'dailyGain':
            case 'growthIndex':
                return reference[METRIC_FIGURE_FIELD[key]] === null ? null : figure(METRIC_FIGURE_FIELD[key]);
            case 'water': {
                // Named line by line as the primary label names them, and for the same reason: the two
                // columns are drawn one behind the other, so a bare pair leaves the reader to remember
                // which figure was the measurement and which the target.
                const lines: string[] = [];
                if (input.toggles.value.water && reference.waterActual !== null) lines.push(`${metricLabel('water')} ${figure('waterActual')}`);
                if (input.toggles.value.waterNorm && reference.waterNorm !== null) lines.push(`${metricLabel('waterNorm')} ${figure('waterNorm')}`);
                return lines.length === 0 ? null : lines.join('<br/>');
            }
            case 'deadCount':
                return reference.deadCount === null || reference.deadCount === 0 ? null : figure('deadCount');
            default:
                return null;
        }
    }

    // Which value a label is placed at. Water is the case this has to be asked for at all: its actual
    // column is drawn capped at the norm, so a label on the point's own y sits inside the stack
    // whenever actual runs over norm, and the pair's label belongs at the top of the two. Asked for
    // every metric rather than only that one, because a reference label is placed on the reference's
    // own value and never on the point's - the point is only there for its axes.
    function figuresAnchor(key: MetricKey, values: MetricFigures): number | undefined {
        if (key === 'water') {
            const candidates = [values.waterActual, values.waterNorm].filter((value): value is number => value !== null);
            return candidates.length > 0 ? Math.max(...candidates) : undefined;
        }
        return values[METRIC_FIGURE_FIELD[key]] ?? undefined;
    }

    /**
     * Moves the row's values, the crosshair and the per-series hover dots to the bucket nearest
     * `timestampMs` - shared by the mousemove handler and the mouse-out/initial reset below, so the
     * row's resting state ("now") is produced by exactly the same path as a hovered position, just fed
     * a different instant.
     */
    function showReadoutAt(timestampMs: number, pointing: boolean): void {
        lastReadoutTimestampMs = timestampMs;
        isPointing.value = pointing;
        readout.value = readoutAt(input.buckets.value, timestampMs, input.granularity.value);
        // Resolved from the row's own instant rather than from the raw cursor position, so the two
        // halves of every figure describe the same point in the Production Group.
        const comparison = input.comparison.value;
        const referenceStart = input.comparisonProductionGroupStart.value;
        referenceReadout.value =
            comparison !== null && referenceStart !== null && readout.value !== null
                ? referenceReadoutFrom(comparison, referenceStart, readout.value.timestamp, input.granularity.value)
                : null;
        // `chart` holds whatever instance was last handed over, and the panel swaps the chart out
        // entirely for its loading, empty and failed states - so a readout can still be asked for
        // while the instance behind this reference has already been destroyed. A destroyed chart keeps
        // its shape but loses its axes, which is why this tests for the axis rather than the chart.
        const chartInstance = chart.value;
        const values = readout.value;
        if (!chartInstance || !chartInstance.xAxis?.[0] || !values) return;

        const bucketMs = values.timestamp;
        const points: Point[] = [];
        const labels: CursorLabel[] = [];
        // Looked up by series id rather than by array position, since a toggle changes which series
        // are visible but never their position in the chart's own series array.
        for (const key of METRIC_KEYS) {
            // `color` is the colour Highcharts resolved for the series, which its own `Series` type
            // does not declare even though every instance carries it.
            const series = chartInstance.get(PRODUCTIVITY_METRICS[key].seriesId) as (Series & { color?: string }) | undefined;
            // Each series is asked at the instant its own figures are keyed at, not at the hovered
            // one - see metricAnchorMs. Anywhere inside a day therefore finds that day's dead count.
            const anchorMs = metricAnchorMs(key, values);
            const point = series?.visible && anchorMs !== null ? series.points.find((candidate) => candidate.x === anchorMs) : undefined;
            if (point) {
                points.push(point);
                const text = cursorLabelText(key, values);
                if (text !== null) {
                    labels.push({ point, text, color: String(series?.color ?? CHART_CHROME_COLORS.cursorLabelFallback), anchorValue: figuresAnchor(key, values) });
                }

                // The reference's own figure, placed on the halo rather than beside the reading above
                // it: the halo shares this series' axis - that is what makes it comparable at all - so
                // the primary point is all that is needed to put a number on the reference's own
                // curve. Which is also what identifies it, and why it needs no name of its own.
                const reference = referenceReadout.value;
                const referenceText = reference === null ? null : referenceCursorLabelText(key, reference, values);
                const referenceAnchor = reference === null ? undefined : figuresAnchor(key, reference);
                if (referenceText !== null && referenceAnchor !== undefined) {
                    labels.push({ point, text: referenceText, color: COMPARISON_REFERENCE_TEXT_COLOR, anchorValue: referenceAnchor });
                }
            }
        }
        productivityOverlay(chartInstance).render({ cursor: { bucketMs, pointing, pinned: isPinned.value, points, labels } });
    }

    // The resting state: the pinned instant while something is pinned, otherwise the most recent
    // bucket that actually holds a reading - read the same way a hovered position is, so its crosshair
    // and dots sit on a real reading rather than disappearing outright when the cursor leaves. `now` is
    // only the fallback's fallback, for a range with no readings at all in it, where the row has
    // nothing to show either way and the date may as well be today's.
    function resetReadout(): void {
        // `isPinned` as the pointing flag: a pin survives the cursor leaving the chart, and is exactly
        // the case where the crosshair has to stay to say the row is being held.
        showReadoutAt(
            pinnedTimestampMs.value ?? latestReadingTimestamp(input.buckets.value, input.toggles.value, input.granularity.value) ?? Date.now(),
            isPinned.value
        );
    }

    function releasePin(): void {
        pinnedTimestampMs.value = null;
        resetReadout();
    }

    // A pin describes one bucket of one Section over one range. Change any of those and it is pointing
    // at something the reader never chose, so it is dropped rather than silently re-aimed.
    function dropPin(): void {
        releasePin();
        // The new range brings its own readings, so the resting position has to be found again against
        // those rather than kept from the range being left behind.
        lastReadoutTimestampMs = null;
    }

    function timestampUnderCursor(event: MouseEvent): number | null {
        if (!chart.value) return null;
        const normalized = chart.value.pointer?.normalize(event);
        if (!normalized) return null;
        return chart.value.xAxis[0].toValue(normalized.chartX);
    }

    function onChartMouseMove(event: MouseEvent): void {
        // Before the pinned early-return: pinning holds the *readout row* still, and has nothing to say
        // about an event tooltip left hanging over the data.
        if (chart.value) input.eventTooltip.hideWhenAway(chart.value, event);
        if (isPinned.value) return;
        const timestampMs = timestampUnderCursor(event);
        if (timestampMs !== null) showReadoutAt(timestampMs, true);
    }

    function onPointerOver(event: MouseEvent): void {
        if (chart.value) input.eventTooltip.recordPointerX(chart.value, event);
    }

    function onChartClick(event: MouseEvent): void {
        const timestampMs = timestampUnderCursor(event);
        if (timestampMs === null) return;
        // Compared by which bucket the row would name, not by raw instant: clicking "the same place"
        // is never the same pixel twice, so an exact-instant comparison would make releasing the pin
        // a matter of luck. Resolved through the row's own lookup rather than a nearest-bucket call of
        // its own, so the click and the row can never disagree about which bucket was clicked.
        const clickedBucketMs = readoutAt(input.buckets.value, timestampMs, input.granularity.value)?.timestamp ?? null;
        const pinnedBucketMs =
            pinnedTimestampMs.value === null ? null : readoutAt(input.buckets.value, pinnedTimestampMs.value, input.granularity.value)?.timestamp ?? null;
        const alreadyPinned = clickedBucketMs !== null && clickedBucketMs === pinnedBucketMs;
        pinnedTimestampMs.value = alreadyPinned ? null : timestampMs;
        showReadoutAt(pinnedTimestampMs.value ?? timestampMs, true);
    }

    function onChartReady(instance: unknown): void {
        const chartInstance = instance as Chart;
        // Dropped rather than cleared: this fires after a re-render, so every point the overlay holds
        // belongs to the render that has just been thrown away. There is no state left on them to
        // clear, and the fresh marks are placed by showReadoutAt below.
        productivityOverlay(chartInstance).forgetHoverMarks();
        chart.value = chartInstance;
        // Resolved here rather than at declaration: the resting position is the latest bucket carrying a
        // reading, and at module scope there are no buckets yet - resolved there, the row opens on
        // today's date beside a row of em-dashes even for a Section whose readings ended weeks ago.
        if (lastReadoutTimestampMs === null) resetReadout();
        else showReadoutAt(lastReadoutTimestampMs, isPinned.value);
    }

    let wiredContainer: HTMLElement | null = null;

    function unwireContainer(): void {
        if (!wiredContainer) return;
        wiredContainer.removeEventListener('mousemove', onChartMouseMove, true);
        wiredContainer.removeEventListener('mouseover', onPointerOver, true);
        wiredContainer.removeEventListener('mouseleave', resetReadout);
        wiredContainer.removeEventListener('click', onChartClick);
        wiredContainer = null;
    }

    // The hover listeners follow whichever container the chart is currently drawn in: off the old one,
    // onto the new one, each time `chart` changes.
    watch(chart, (instance) => {
        unwireContainer();
        if (instance?.container) {
            // Capture phase, so this runs before Highcharts' own mousemove on the same container:
            // it records the cursor's x, which the tooltip's positioner needs during the very move that
            // opens a card - by the bubble phase the card has already been placed.
            instance.container.addEventListener('mousemove', onChartMouseMove, true);
            instance.container.addEventListener('mouseover', onPointerOver, true);
            instance.container.addEventListener('mouseleave', resetReadout);
            instance.container.addEventListener('click', onChartClick);
            instance.container.style.cursor = 'crosshair';
            wiredContainer = instance.container;
        }
    });

    onBeforeUnmount(() => {
        unwireContainer();
        if (chart.value) productivityOverlay(chart.value).destroy();
    });

    return {
        readout,
        referenceReadout,
        isPinned,
        isPointing,
        releasePin,
        dropPin,
        onChartReady,
    };
}
